import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { Mail, Phone, MapPin } from 'lucide-react';
import Navbar from '../components/Navbar';
import Footer from '../components/Footer';
import axios from 'axios';
import whobanner from "../img/quote.png";

const API_URL = 'https://api.securxperts.com:8000/forms/quotation';

const RequestQuoteDesign = () => {

  const [formData, setFormData] = useState({
    name: '',
    email: '',
    countrycode: '+91',
    phone: '',
    service: '',
    message: '',
  });

  const [status, setStatus] = useState('');
  const [isSubmitting, setIsSubmitting] = useState(false);

  const [showScroll, setShowScroll] = useState(false);
  const [scrollProgress, setScrollProgress] = useState(0);

  useEffect(() => {
    const handleScroll = () => {
      const totalHeight = document.body.scrollHeight - window.innerHeight;
      const progress = totalHeight > 0 ? (window.scrollY / totalHeight) * 100 : 0;
      setScrollProgress(progress);
      setShowScroll(window.scrollY > 300);
    };
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  const scrollToTop = () => window.scrollTo({ top: 0, behavior: 'smooth' });

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));
  };

  const safeStringify = (v, max = 1200) => {
    try {
      const s = typeof v === 'string' ? v : JSON.stringify(v, null, 2);
      return s.length > max ? s.slice(0, max) + '...' : s;
    } catch {
      return String(v);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setStatus('Sending...');
    setIsSubmitting(true);

    // ---- Dynamic phone validation by country ----
    const rawPhone = formData.phone || '';
    const digits = rawPhone.replace(/\D/g, '');
    const ccode = formData.countrycode || '+91';

    let valid = true;
    let phoneError = '';

    switch (ccode) {
      case '+91': // India
        valid = /^[6-9]\d{9}$/.test(digits);
        phoneError = 'Please enter a valid 10-digit Indian mobile number.';
        break;
      case '+44': // UK
        valid = /^\d{10,11}$/.test(digits);
        phoneError = 'Please enter a valid UK phone number (10–11 digits).';
        break;
      case '+1': // US/Canada
        valid = /^\d{10}$/.test(digits);
        phoneError = 'Please enter a valid 10-digit US/Canada phone number.';
        break;
      case '+61': // Australia
        valid = /^\d{9}$/.test(digits) || /^\d{10}$/.test(digits);
        phoneError = 'Please enter a valid Australian phone number (9–10 digits).';
        break;
      default:
        valid = digits.length >= 7;
        phoneError = 'Please enter a valid phone number.';
        break;
    }

    if (!valid) {
      setStatus(phoneError);
      setIsSubmitting(false);
      return;
    }
    // --------------------------------------------

    const payload = {
      name: formData.name,
      email: formData.email,
      country_code: ccode,
      phoneno: digits,
      service: formData.service,
      message: formData.message,
    };

    try {
      const response = await axios.post(API_URL, payload, {
        headers: { 'Content-Type': 'application/json' },
        timeout: 15000,
        validateStatus: () => true,
      });

      if (response.status === 200 && response.data && response.data.success) {
        // success state
        setStatus('Submitted Successfully');

        setFormData({
          name: '',
          email: '',
          countrycode: '+91',
          phone: '',
          service: '',
          message: '',
        });

        // auto-refresh after short delay
        setTimeout(() => {
          window.location.reload();
        }, 1500);
      } else {
        const msg =
          response.data?.detail ||
          response.data?.message ||
          response.data?.error ||
          response.data;
        setStatus(`${safeStringify(msg)}`);
      }

    } catch (err) {
      if (err.response) {
        setStatus(`Failed. Server returned ${err.response.status}: ${safeStringify(err.response.data)}`);
      } else if (err.request) {
        setStatus('Network error — no response. Check server/CORS.');
      } else {
        setStatus(`Error: ${err.message}`);
      }
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <div className="min-h-screen flex flex-col bg-gray-50">
      <Navbar />

      {/* Banner */}
      <header className="relative w-full">
        <div
          className="relative w-full h-[317px] bg-center bg-cover bg-no-repeat"
          style={{ backgroundImage: `url(${whobanner})` }}
        >
          <div className="absolute inset-0 bg-black/70 lg:bg-black/60" />

          <div className="absolute inset-0 flex flex-col items-center justify-center text-white text-center px-4 z-10">
            <h1 className="text-3xl sm:text-4xl md:text-5xl font-bold mb-4 drop-shadow-md">
              Request Quote
            </h1>

            <div className="flex items-center gap-2 text-sm sm:text-base">
              <Link to="/" className="hover:underline">Home</Link>
              <svg width="7" height="10" fill="none">
                <path
                  d="M0.297 0.262C0.105 0.426 -0.002 0.648 0 0.878C0.002 1.109 0.113 1.329 0.308 1.492L4.02 4.604L0.366 7.711C0.173 7.875 0.067 8.097 0.069 8.328C0.071 8.558 0.182 8.778 0.377 8.941C0.474 9.022 0.59 9.086 0.717 9.13C0.844 9.174 0.981 9.196 1.118 9.195C1.255 9.194 1.391 9.171 1.518 9.126C1.644 9.081 1.758 9.016 1.854 8.933L6.232 5.212C6.329 5.131 6.405 5.034 6.457 4.927C6.509 4.821 6.535 4.706 6.534 4.591C6.533 4.476 6.505 4.362 6.451 4.256C6.397 4.15 6.319 4.054 6.221 3.974L1.774 0.254C1.677 0.173 1.561 0.109 1.434 0.065C1.307 0.021 1.171 -0.001 1.033 0C0.896 0.001 0.76 0.024 0.634 0.069C0.507 0.114 0.393 0.18 0.297 0.262Z"
                  fill="white"
                />
              </svg>
              <span>Quote</span>
            </div>
          </div>
        </div>
      </header>

      {/* Form Section */}
      <section className="flex-grow py-12 sm:py-16 md:py-20 lg:py-24 bg-gradient-to-b from-gray-50 to-gray-100">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">

          <div
            className="flex flex-col lg:flex-row rounded-3xl shadow-xl overflow-hidden bg-white mx-auto"
            style={{ minHeight: '581px', maxWidth: '1216px' }}
          >

            {/* Left Panel */}
            <div className="w-full lg:w-5/12 p-8 sm:p-12 md:p-16 bg-[#282C6A] text-white relative">
              <h2 className="text-3xl sm:text-4xl font-extrabold">REQUEST A QUOTE</h2>
              <p className="text-base sm:text-lg mt-4">Need a Free Quote? Please Feel Free to Contact Us</p>

              <div className="space-y-4 pt-6">
                <div className="flex items-center gap-3">
                  <Phone className="w-6 h-6 text-[#A0C4FF]" />
                  <span>+91 79932 56679</span>
                </div>
                <div className="flex items-center gap-3">
                  <Mail className="w-6 h-6 text-[#A0C4FF]" />
                  <a href="mailto:info@securxperts.com" className="underline">info@securxperts.com</a>
                </div>
                <div className="flex items-start gap-3">
                  <MapPin className="w-6 h-6 text-[#A0C4FF]" />
                  <p>
                    5th Floor, Krishna Towers<br />
                    (Square Hub), 100 Feet Rd,<br />
                    Madhapur, Hyderabad,<br />
                    Telangana 500081
                  </p>
                </div>

                {/* UK Office added */}
                <div className="flex items-start gap-3 mt-4">
                  <MapPin className="w-6 h-6 text-[#A0C4FF]" />
                  <p>
                    UK Office:<br></br>
194 Gulson Road,
Coventry, England, CV1 2JD
United Kingdom
                    {/* Replace with exact UK address when available */}
                  </p>
                </div>
              </div>
            </div>

            {/* Right Panel */}
            <div className="w-full lg:w-7/12 p-8 sm:p-12 md:p-16 bg-white">

              {status && (
                <div
                  className={`mb-6 p-4 text-center rounded font-medium text-sm ${
                    status === 'Submitted Successfully'
                      ? 'bg-blue-100 text-blue-700'
                      : 'bg-red-100 text-red-700'
                  }`}
                >
                  {status}
                </div>
              )}

              <form onSubmit={handleSubmit} className="space-y-8">

                {/* Name */}
                <input
                  type="text"
                  name="name"
                  value={formData.name}
                  onChange={handleChange}
                  placeholder="Full Name"
                  className="w-full border-b border-gray-300 py-2 text-lg focus:border-[#282C6A]"
                  required
                />

                {/* Email */}
                <input
                  type="email"
                  name="email"
                  value={formData.email}
                  onChange={handleChange}
                  placeholder="Email"
                  className="w-full border-b border-gray-300 py-2 text-lg focus:border-[#282C6A]"
                  required
                />

                {/* Country code + phone combined + service */}
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-8">
                  <div className="flex items-center gap-4">
                    <select
                      name="countrycode"
                      value={formData.countrycode}
                      onChange={handleChange}
                      className="w-40 border-b border-gray-300 py-2 text-lg bg-white text-gray-700 focus:border-[#282C6A]"
                      required
                    >
                      <option value="+91">🇮🇳 India (+91)</option>
                      <option value="+44">🇬🇧 United Kingdom (+44)</option>
                      <option value="+1">🇺🇸 United States (+1)</option>
                      <option value="+1">🇨🇦 Canada (+1)</option>
                      <option value="+61">🇦🇺 Australia (+61)</option>
                    </select>

                    <input
                      type="tel"
                      name="phone"
                      value={formData.phone}
                      onChange={handleChange}
                      placeholder="Enter phone number"
                      className="flex-1 border-b border-gray-300 py-2 text-lg focus:border-[#282C6A]"
                      required
                    />
                  </div>
                  <br></br>

                  <select
                    name="service"
                    value={formData.service}
                    onChange={handleChange}
                    className="w-full border-b border-gray-300 py-2 text-lg bg-white text-gray-600 focus:border-[#282C6A]"
                    required
                  >
                    <option value="" disabled>-- Select Service --</option>
                    <option value="web">Web Development</option>
                    <option value="app">Mobile App Development</option>
                    <option value="design">UI/UX Design</option>
                    <option value="marketing">Digital Marketing</option>
                    <option value="consulting">Consulting</option>
                    <option value="other">Other</option>
                  </select>
                </div>

                {/* Message */}
                <textarea
                  name="message"
                  value={formData.message}
                  onChange={handleChange}
                  placeholder="Message"
                  rows="5"
                  className="w-full border-b border-gray-300 py-2 text-lg resize-none focus:border-[#282C6A]"
                  required
                />

                {/* Submit */}
                <button
                  type="submit"
                  disabled={isSubmitting}
                  className="w-full max-w-[280px] bg-gradient-to-r from-blue-600 to-indigo-600 text-white py-4 rounded-lg shadow-md hover:from-blue-700 hover:to-indigo-700 text-lg disabled:opacity-60"
                >
                  {isSubmitting ? 'Sending...' : 'Submit'}
                </button>

              </form>
            </div>
          </div>
        </div>
      </section>

      <Footer />

      {/* Scroll To Top */}
      {showScroll && (
        <div
          onClick={scrollToTop}
          className="fixed bottom-6 right-6 z-50 cursor-pointer flex items-center justify-center w-14 h-14 rounded-full bg-gradient-to-r from-blue-500 to-blue-700 shadow-lg"
        >
          <svg className="progress-circle -rotate-90 w-12 h-12 absolute" viewBox="-1 -1 102 102">
            <path
              d="M50,1 a49,49 0 0,1 0,98 a49,49 0 0,1 0,-98"
              fill="none"
              stroke="white"
              strokeWidth="4"
              strokeDasharray="307.919"
              strokeDashoffset={307.919 - (307.919 * scrollProgress) / 100}
            />
          </svg>

          <svg className="w-6 h-6 text-white relative z-10" viewBox="0 0 24 24" fill="none">
            <path stroke="currentColor" strokeWidth={2} strokeLinecap="round" strokeLinejoin="round" d="M5 15l7-7 7 7" />
          </svg>
        </div>
      )}
    </div>
  );
};

export default RequestQuoteDesign;
